import React, { useContext, ReactElement } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Context } from './context';

import { TipLevels, VALIDATION_TYPE_REQUIRED } from '@unitedtraders/validators';
import { FinalFormFieldProps, Hints } from './types';

interface ErrorMessage {
  message: string;
  details?: Record<string, string>;
}

type SubmitError = string | ErrorMessage;

export const FinalFormField = ({
  afterSubmit,
  allowNull,
  beforeSubmit,
  children,
  component,
  defaultValue,
  format,
  formatOnBlur,
  initialValue,
  isEqual,
  multiple,
  name,
  parse,
  subscription,
  type,
  validate,
  validateFields,
  value,
  hintInfo,
  hintWarning,
  hintError,
  ...rest
}: FinalFormFieldProps): ReactElement => {
  const field = useField(name, {
    afterSubmit,
    allowNull,
    beforeSubmit,
    defaultValue,
    format,
    formatOnBlur,
    initialValue,
    isEqual,
    multiple,
    parse,
    subscription,
    type,
    validate,
    validateFields,
    value
  });

  const {
    meta: { active, error, touched }
  } = field;

  const submitError = field.meta.submitError as SubmitError;

  const { tips, submittedAtLeastOneTime } = useContext(Context);
  const tip = tips && tips[name];
  const hints: Hints = {};
  const { t } = useTranslation();

  if (tip) {
    const translatedTip = t(tip.key, tip.options);

    switch (tip?.level) {
      case TipLevels.TIP:
        hints.hintInfo = active ? translatedTip : undefined;
        break;
      case TipLevels.WARNING:
        hints.hintWarning = translatedTip;
        break;
      default:
        break;
    }
  }

  if (error) {
    const hintError =
      error.key === VALIDATION_TYPE_REQUIRED
        ? submittedAtLeastOneTime && t(error.key, error.options)
        : touched && t(error.key, error.options);

    hints.hintError = hintError;
  }

  if (hintInfo) {
    hints.hintInfo = active && hintInfo;
  }

  if (hintWarning) {
    hints.hintWarning = hintWarning;
  }

  if (hintError) {
    hints.hintError = touched && submittedAtLeastOneTime && hintError;
  }

  if (submitError) {
    if (typeof submitError === 'object') {
      hints.hintError = t(submitError.message, submitError.details);
    } else {
      hints.hintError = t(submitError);
    }
  }

  if (component) {
    return React.createElement(component, { ...field, ...rest, ...hints });
  }

  if (typeof children !== 'function') {
    throw new Error(
      `Must specify either a render prop, a render function as children, or a component prop to ${name}`
    );
  }

  return children({ ...field, ...rest, ...hints });
};
